import { buildMsTemplate, buildTemplate, GridTemplateInputToken } from './grid-template-tokens';

type GridRegions = { [key in string]: [number, number, number, number] };

/**
 * Strings may have a variety of values as units. Numbers are just pixel values. Null behaves the
 * same as 0.
 */
export type GridGap = string | null | number;
export type GridGapConfig = GridGap | [GridGap, GridGap];

/**
 * Ensures that the grid can be used in its current state. Identity function when valid.
 * @param grid A two-dimensional array of regions
 * @returns the input parameter (no changes)
 */
export const validateGridDimensions = (grid: string[][]) => {
  grid.reduce((acc, curr, i) => {
    if (curr.length === 0) throw Error(`Rows must have at least one element. Empty row at [${i}]`);
    if (acc && acc !== curr.length) throw Error(`Rows must be of same length. Bad row: ${curr}`);
    return curr.length;
  }, 0);
  return grid;
};

// CSS Units: https://drafts.csswg.org/css-values-4/#lengths
// Cross-referenced with compatibility table: https://developer.mozilla.org/en-US/docs/Web/CSS/length
const UNITS = ['em', 'ex', 'ch', 'rem', 'vw', 'vh', 'vmin', 'cm', 'mm', 'in', 'pt', 'pc', 'px'];
const VALID_LENGTH_REGEXP = new RegExp(`^\\d+\\.?\\d*(${UNITS.join('|')})$`);

/**
 * Ensures that the gap configuration supplied is valid for cross-browser support.
 * @param gapConfig A string or tuple of gap sizes
 * @returns the input parameter (no changes)
 * @throws will throw if the configuration has invalid units or is of an invalid form
 */
export const validateGapConfig = (gapConfig: string | 0 | null | GridGapConfig) => {
  const config = Array.isArray(gapConfig) ? gapConfig : [gapConfig, gapConfig];

  return config.map((gap) => {
    if (!gap || gap === '0') return null;
    if (typeof gap === 'number') return `${gap}px`;
    if (!VALID_LENGTH_REGEXP.test(gap)) {
      throw Error(
        `Invalid grid gap [${gap}]. Gap must be null, empty, '0', or a length defined using cross-browser-capable units: ${UNITS.join()}.`,
      );
    }
    return gap;
  });
};

/**
 * Transforms a two-dimensional array of areas into named regions with locations in a grid. These
 * named regions are use by the children of a grid component to position the elements appropriately
 * within the parent context.
 * @param grid A two dimensional array of named areas for representation in a grid
 */
export const gridToAreas = (grid: string[][]) => ({
  regions: grid.reduce(
    (measures, row, rowIndex) =>
      row.reduce((rowMeasures, entry, colIndex) => {
        if (!rowMeasures[entry]) {
          return {
            ...rowMeasures,
            [entry]: [rowIndex, colIndex, rowIndex, colIndex],
          } as GridRegions;
        }

        const [rowStart, colStart, rowEnd, colEnd] = rowMeasures[entry];

        const nextRowEnd = Math.max(rowIndex, rowEnd);
        const nextColStart = Math.min(colIndex, colStart);
        const nextColEnd = Math.max(colIndex, colEnd);

        return {
          ...rowMeasures,
          [entry]: [rowStart, nextColStart, nextRowEnd, nextColEnd],
        } as GridRegions;
      }, measures),
    {} as GridRegions,
  ),
  dimensions: [grid.length, grid[0].length],
});

/**
 * Converts areas into CSS to be used by children.
 * In Internet Explorer, we create additional rows and columns to simulate a gap between items.
 * @param areas An object with a region key paired with coordinates on the grid
 */
export const areasToChildStyles = (areas: GridRegions, gapConfig: GridGapConfig = [null, null]) =>
  Object.keys(areas).reduce((acc, area) => {
    const [rowGap, colGap] = Array.isArray(gapConfig) ? gapConfig : [gapConfig, gapConfig];

    // CSS grid uses a 1-based index
    const [rowStart, colStart, rowEnd, colEnd] = areas[area].map((x) => x + 1);

    // This is the arithmetic required to include simulated rows and columns in our IE grid.
    const insertGaps = (start: number, end: number, gap: GridGapConfig) => {
      const normalizedStart = gap ? 2 * start - 1 : start;
      const normalizedEnd = gap ? 2 * end - 1 : end;
      return {
        start: normalizedStart,
        span: normalizedEnd - normalizedStart + 1,
      };
    };

    const { start: gappedColStart, span: gappedColSpan } = insertGaps(colStart, colEnd, colGap);
    const { start: gappedRowStart, span: gappedRowSpan } = insertGaps(rowStart, rowEnd, rowGap);

    return {
      ...acc,
      [area]: `
        -ms-grid-row: ${gappedRowStart};
        -ms-grid-row-span: ${gappedRowSpan};
        -ms-grid-column: ${gappedColStart};
        -ms-grid-column-span: ${gappedColSpan};
        grid-area: ${area};
    `,
    };
  }, {});

/**
 * Generates the parent styles from the grid dimensions and area description.
 * In Internet Explorer, we create additional rows and columns to simulate a gap between items.
 *
 * @param rows The number of rows in the grid
 * @param cols The number of columns in the grid
 * @param areaString The template area string describing the grid
 */
export const areasToParentStyles = (
  rows: number,
  cols: number,
  areaTemplate: string,
  gapConfig: GridGapConfig | string | 0 | null,
  rowTemplate?: GridTemplateInputToken[],
  columnTemplate?: GridTemplateInputToken[],
) => {
  const [rowGap, colGap] = validateGapConfig(gapConfig);

  const msGridRows = buildMsTemplate(rows, rowTemplate, rowGap);
  const msGridColumns = buildMsTemplate(cols, columnTemplate, colGap);

  const gridRows = buildTemplate(rowTemplate, rows);
  const gridColumns = buildTemplate(columnTemplate, cols);

  return `
    display: -ms-grid;
    display: grid;
    -ms-grid-rows: ${msGridRows};
    grid-template-rows: ${gridRows};
    -ms-grid-columns: ${msGridColumns};
    grid-template-columns: ${gridColumns};
    grid-template-areas: ${areaTemplate};
    grid-row-gap: ${rowGap || 'normal'};
    grid-column-gap: ${colGap || 'normal'};
    `;
};

export function gridTemplateToStyles(
  areaTemplate: string[][],
  gap: GridGapConfig = [null, null],
  rowTemplate?: GridTemplateInputToken[],
  columnTemplate?: GridTemplateInputToken[],
) {
  const gridAreas = gridToAreas(validateGridDimensions(areaTemplate));

  const {
    regions,
    dimensions: [rows, cols],
  } = gridAreas;

  const childStyles = areasToChildStyles(regions, gap);
  const parentStyles = areasToParentStyles(
    rows,
    cols,
    areaTemplate.reduce((acc, row) => `${acc}'${row.join(' ')}'`, ''),
    gap,
    rowTemplate,
    columnTemplate,
  );

  return {
    childStyles,
    parentStyles,
  };
}
